<template>
    <div class="w-full">
        <!-- Search Bar with Advanced Search Button -->
        <div class="flex items-center space-x-3">
            <!-- Basic Search Input -->
            <div class="flex-1 relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <font-awesome-icon :icon="['fas', 'search']" class="h-4 w-4 text-gray-400" />
                </div>
                <input type="text" v-model="basicSearchText" placeholder="Search by Name, Student ID, or Emirates ID"
                    class="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    @input="onBasicSearch" />
            </div>

            <!-- Advanced Search Button -->
            <button @click="openAdvancedSearchModal"
                class="flex items-center px-4 py-2.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200">
                <font-awesome-icon :icon="['fas', 'sliders-h']" class="h-4 w-4 mr-2" />
                Advanced Search
            </button>
        </div>

        <!-- Advanced Search Modal -->
        <transition name="modal">
            <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto">
                <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    <!-- Background overlay -->
                    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="closeModal"></div>

                    <!-- Modal panel -->
                    <div
                        class="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                        <!-- Modal Header -->
                        <div class="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                            <div class="flex items-center justify-between">
                                <div class="flex items-center">
                                    <font-awesome-icon :icon="['fas', 'search-plus']" class="h-5 w-5 text-white mr-3" />
                                    <h3 class="text-lg font-semibold text-white">Advanced Search</h3>
                                </div>
                                <button @click="closeModal"
                                    class="text-white hover:text-gray-200 focus:outline-none focus:text-gray-200 transition-colors duration-200">
                                    <font-awesome-icon :icon="['fas', 'times']" class="h-5 w-5" />
                                </button>
                            </div>
                        </div>

                        <!-- Modal Body -->
                        <div class="bg-white px-6 py-6">
                            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <!-- Employment Section -->
                                <div class="space-y-4">
                                    <h4 class="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2">
                                        Employment Information</h4>

                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-2">Employer</label>
                                        <input type="text" v-model="advancedSearch.employer"
                                            placeholder="Enter employer name"
                                            class="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200" />
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-2">Designation</label>
                                        <input type="text" v-model="advancedSearch.emp_pos"
                                            placeholder="Enter job position"
                                            class="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200" />
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-2">Employer
                                            Location</label>
                                        <input type="text" v-model="advancedSearch.emp_loc"
                                            placeholder="Enter employer location"
                                            class="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200" />
                                    </div>
                                </div>

                                <!-- Education Section -->
                                <div class="space-y-4">
                                    <h4 class="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2">
                                        Education Information</h4>

                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-2">University
                                            Name</label>
                                        <input type="text" v-model="advancedSearch.university_name"
                                            placeholder="Enter university name"
                                            class="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200" />
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-2">College</label>
                                        <input type="text" v-model="advancedSearch.college"
                                            placeholder="Enter college name"
                                            class="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200" />
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-2">Major</label>
                                        <input type="text" v-model="advancedSearch.major" placeholder="Enter major"
                                            class="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200" />
                                    </div>
                                </div>

                                <!-- Program & Other Section -->
                                <div class="space-y-4">
                                    <h4 class="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2">
                                        Program & Other</h4>

                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-2">Program Name</label>
                                        <input type="text" v-model="advancedSearch.program_name"
                                            placeholder="Enter program name"
                                            class="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200" />
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-2">Sponsor</label>
                                        <input type="text" v-model="advancedSearch.sponsor" placeholder="Enter sponsor"
                                            class="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200" />
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-2">Campus</label>
                                        <input type="text" v-model="advancedSearch.campus_code"
                                            placeholder="Enter campus code"
                                            class="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Modal Footer -->
                        <div class="bg-gray-50 px-6 py-4 flex justify-between items-center">
                            <div class="flex items-center text-sm text-gray-500">
                                <font-awesome-icon :icon="['fas', 'info-circle']" class="h-4 w-4 mr-2" />
                                Use any combination of fields to refine your search
                            </div>
                            <div class="flex space-x-3">
                                <button @click="clearAdvancedSearch"
                                    class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200">
                                    Clear All
                                </button>
                                <button @click="applyAdvancedSearch"
                                    class="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200">
                                    Apply Search
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
export default {
    name: "AdvancedSearch",
    props: {
        initialBasicSearch: {
            type: String,
            default: ""
        },
        initialAdvancedSearch: {
            type: Object,
            default: () => ({})
        }
    },
    emits: ["on-basic-search", "on-advanced-search", "on-clear-search"],
    data() {
        return {
            showModal: false,
            basicSearchText: this.initialBasicSearch,
            advancedSearch: {
                employer: "",
                emp_pos: "",
                emp_loc: "",
                university_name: "",
                college: "",
                major: "",
                program_name: "",
                sponsor: "",
                campus_code: "",
                ...this.initialAdvancedSearch
            }
        };
    },
    watch: {
        initialBasicSearch(newVal) {
            this.basicSearchText = newVal;
        },
        initialAdvancedSearch: {
            handler(newVal) {
                this.advancedSearch = { ...this.advancedSearch, ...newVal };
            },
            deep: true
        }
    },
    methods: {
        openAdvancedSearchModal() {
            this.showModal = true;
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';
        },
        closeModal() {
            this.showModal = false;
            // Restore body scroll
            document.body.style.overflow = 'auto';
        },
        onBasicSearch() {
            this.$emit("on-basic-search", this.basicSearchText);
        },
        applyAdvancedSearch() {
            this.$emit("on-advanced-search", this.advancedSearch);
            this.closeModal();
        },
        clearAdvancedSearch() {
            this.advancedSearch = {
                employer: "",
                emp_pos: "",
                emp_loc: "",
                university_name: "",
                college: "",
                major: "",
                program_name: "",
                sponsor: "",
                campus_code: ""
            };
            this.$emit("on-advanced-search", this.advancedSearch);
        },
        clearAllSearch() {
            this.basicSearchText = "";
            this.clearAdvancedSearch();
            this.$emit("on-basic-search", "");
            this.$emit("on-clear-search");
        }
    },
    beforeUnmount() {
        // Ensure body scroll is restored when component is destroyed
        document.body.style.overflow = 'auto';
    }
};
</script>

<style lang="scss" scoped>
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

.modal-enter-active .inline-block,
.modal-leave-active .inline-block {
    transition: transform 0.3s ease;
}

.modal-enter-from .inline-block,
.modal-leave-to .inline-block {
    transform: scale(0.95) translateY(-20px);
}

/* Custom scrollbar for modal */
.modal-body::-webkit-scrollbar {
    width: 6px;
}

.modal-body::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

/* Focus styles for better accessibility */
input:focus {
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Button hover effects */
button:hover {
    transform: translateY(-1px);
}

button:active {
    transform: translateY(0);
}
</style>